import {
	Card,
	Descriptions,
	Tag,
	Button,
	Space,
	Typography,
	notification,
	Spin,
	Tabs,
	Table,
	Empty,
	Avatar,
	Badge,
	Modal,
	Form,
	Input,
	Select,
	Row,
	Col,
	Popconfirm,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	requestGetCustomerById,
	requestGetOrderList,
	requestAddCustomerAddress,
	requestUpdateCustomerAddress,
	requestDeleteCustomerAddress,
} from "../../services/admin.service";
import {
	PiArrowLeftDuotone,
	PiPencilDuotone,
	PiEyeDuotone,
	PiUserDuotone,
	PiPlusDuotone,
	PiMapPinDuotone,
	PiTrashDuotone,
} from "react-icons/pi";
import { formatCurrency } from "../../utils/Formatter";
import {
	requestGetDOMACustomerList,
	requestAddDOMACustomer,
} from "../../services/doma.admin.service";

const { Title, Text } = Typography;
const { Option } = Select;

function ViewCustomerPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [customerData, setCustomerData] = useState(null);
	const [orders, setOrders] = useState([]);
	const [ordersLoading, setOrdersLoading] = useState(false);

	// Address management states
	const [addressModalVisible, setAddressModalVisible] = useState(false);
	const [addressForm] = Form.useForm();
	const phone = Form.useWatch("phone", addressForm);
	const [editingAddress, setEditingAddress] = useState(null);
	const [addressLoading, setAddressLoading] = useState(false);

	// Domain registration management
	const [domaCustomers, setDomaCustomers] = useState([]);
	const [domaModalVisible, setDomaModalVisible] = useState(false);
	const [domaForm] = Form.useForm();
	const [domaLoading, setDomaLoading] = useState(false);

	useEffect(() => {
		document.title = "Cloudiva Console | View Customer";
		if (id) {
			fetchCustomerData();
			fetchCustomerOrders();
			fetchDomaCustomerList();
		}
	}, [id]);

	const fetchCustomerData = () => {
		setLoading(true);
		requestGetCustomerById(id)
			.then((response) => {
				setCustomerData(response.data.data);
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch customer data",
				});
				navigate("/customers");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchCustomerOrders = () => {
		setOrdersLoading(true);
		requestGetOrderList({
			limit: 100,
			filter: `customer:${id}`,
			order: "createdAt:desc",
		})
			.then((response) => {
				setOrders(response.data.data || []);
			})
			.catch((error) => {
				console.error("Error fetching customer orders:", error);
				// Don't show error notification for orders as it's not critical
			})
			.finally(() => {
				setOrdersLoading(false);
			});
	};

	const fetchDomaCustomerList = () => {
		setLoading(true);
		requestGetDOMACustomerList({
			filter: `user:${id}`,
		})
			.then((response) => {
				setDomaCustomers(response.data.data || []);
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch domain customers",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	// Address management functions
	useEffect(() => {
		// replace the phone number with right format if start without + add the +, if start with 0, replace with +62
		if (phone && !phone.startsWith("+")) {
			addressForm.setFieldsValue({
				phone: `+${phone}`,
			});
		}
		if (phone && phone.startsWith("0")) {
			addressForm.setFieldsValue({
				phone: phone.replace(/^0/, "+62"),
			});
		}
		if (phone && phone.startsWith("+0")) {
			addressForm.setFieldsValue({
				phone: phone.replace(/^\+0/, "+62"),
			});
		}
	}, [phone]);

	const handleAddAddress = () => {
		setEditingAddress(null);
		addressForm.resetFields();
		setAddressModalVisible(true);
	};

	const handleEditAddress = (address) => {
		setEditingAddress(address);
		addressForm.setFieldsValue({
			...address,
		});
		setAddressModalVisible(true);
	};

	const handleAddressSubmit = async () => {
		try {
			const values = await addressForm.validateFields();
			setAddressLoading(true);

			if (editingAddress) {
				// Update existing address
				await requestUpdateCustomerAddress(id, editingAddress._id, values);
				notification.success({
					message: "Success",
					description: "Address updated successfully",
				});
			} else {
				// Add new address
				await requestAddCustomerAddress(id, values);
				notification.success({
					message: "Success",
					description: "Address added successfully",
				});
			}

			setAddressModalVisible(false);
			fetchCustomerData(); // Refresh customer data to get updated addresses
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.message,
			});
		} finally {
			setAddressLoading(false);
		}
	};

	const handleDeleteAddress = async (addressId) => {
		try {
			setLoading(true);
			await requestDeleteCustomerAddress(id, addressId);
			notification.success({
				message: "Success",
				description: "Address deleted successfully",
			});
			fetchCustomerData(); // Refresh customer data
		} catch (error) {
			notification.error({
				message: "Error",
				description: "Failed to delete address",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleAddressModalCancel = () => {
		setAddressModalVisible(false);
		setEditingAddress(null);
		addressForm.resetFields();
	};

	// Domain customer management functions
	const handleAddDomaCustomer = () => {
		setDomaModalVisible(true);
		domaForm.resetFields();
	};

	const handleDomaCustomerSubmit = async () => {
		try {
			const values = await domaForm.validateFields();
			setDomaLoading(true);

			const payload = {
				userId: id,
				addressId: values.addressId,
				registrarCode: values.registrarCode,
			};

			await requestAddDOMACustomer(payload);
			notification.success({
				message: "Success",
				description: "Domain registration address added successfully",
			});

			setDomaModalVisible(false);
			fetchDomaCustomerList(); // Refresh domain customer list
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "Failed to add domain registration address",
			});
		} finally {
			setDomaLoading(false);
		}
	};

	const handleDomaModalCancel = () => {
		setDomaModalVisible(false);
		domaForm.resetFields();
	};

	const orderColumns = [
		{
			title: "Order ID",
			dataIndex: "orderNumber",
			key: "orderNumber",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>ID: {record._id}</div>
				</div>
			),
		},
		{
			title: "Service",
			dataIndex: "serviceName",
			key: "serviceName",
			render: (text, record) => <Badge count={record.items.length} showZero></Badge>,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				const colors = {
					PENDING: "orange",
					PROCESSING: "blue",
					COMPLETED: "green",
					CANCELLED: "red",
					FAILED: "red",
				};
				return <Tag color={colors[status] || "default"}>{status}</Tag>;
			},
		},
		{
			title: "Grand Total",
			dataIndex: "total",
			key: "total",
			render: (amount, record) => formatCurrency(amount, record.currency || "IDR"),
		},
		{
			title: "Created At",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			align: "right",
			render: (_, record) => (
				<Space size="small">
					<Button
						type="text"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/orders/${record._id}`)}
						title="View Order"
					/>
				</Space>
			),
		},
	];

	if (loading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!customerData) {
		return (
			<div className="page-content">
				<div className="page-content-wrapper">
					<Card>
						<Empty description="Customer not found" />
					</Card>
				</div>
			</div>
		);
	}

	const tabItems = [
		{
			key: "basic-information",
			label: "Basic Information",
			children: (
				<Card>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
						<Avatar
							size={80}
							icon={<PiUserDuotone />}
							src={customerData.avatar}
							style={{ marginRight: 16 }}
						/>
						<div>
							<Title level={3} style={{ margin: 0 }}>
								{customerData.name}
							</Title>
							<Text type="secondary">{customerData.email}</Text>
						</div>
					</div>
					<Descriptions column={2} bordered>
						<Descriptions.Item label="Customer ID">
							<Text code>{customerData._id}</Text>
						</Descriptions.Item>
						<Descriptions.Item label="Customer Type">
							<Tag color={customerData.type === "BUSINESS" ? "blue" : "green"}>
								{customerData.type === "BUSINESS" ? "Business" : "Individual"}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Status">
							<Tag
								color={
									customerData.status === "ACTIVE"
										? "green"
										: customerData.status === "SUSPENDED"
										? "orange"
										: "red"
								}>
								{customerData.status === "ACTIVE"
									? "Active"
									: customerData.status === "SUSPENDED"
									? "Suspended"
									: "Inactive"}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Phone">{customerData.phone || "-"}</Descriptions.Item>
						<Descriptions.Item label="Created At">
							{new Date(customerData.createdAt).toLocaleString()}
						</Descriptions.Item>
						<Descriptions.Item label="Updated At">
							{new Date(customerData.updatedAt).toLocaleString()}
						</Descriptions.Item>
					</Descriptions>

					{/* Personal Information for Individual customers */}
					{customerData.type === "INDIVIDUAL" && customerData.personalInfo && (
						<>
							<Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
								Personal Information
							</Title>
							<Descriptions column={2} bordered>
								{customerData.personalInfo.dateOfBirth && (
									<Descriptions.Item label="Date of Birth">
										{new Date(customerData.personalInfo.dateOfBirth).toLocaleDateString()}
									</Descriptions.Item>
								)}
								{customerData.personalInfo.gender && (
									<Descriptions.Item label="Gender">
										{customerData.personalInfo.gender}
									</Descriptions.Item>
								)}
								{customerData.personalInfo.nationalId && (
									<Descriptions.Item label="National ID">
										{customerData.personalInfo.nationalId}
									</Descriptions.Item>
								)}
							</Descriptions>
						</>
					)}

					{/* Business Information for Business customers */}
					{customerData.type === "BUSINESS" && customerData.businessInfo && (
						<>
							<Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
								Business Information
							</Title>
							<Descriptions column={2} bordered>
								{customerData.businessInfo.company && (
									<Descriptions.Item label="Company Name">
										{customerData.businessInfo.company}
									</Descriptions.Item>
								)}
								{customerData.businessInfo.taxId && (
									<Descriptions.Item label="Tax ID">
										{customerData.businessInfo.taxId}
									</Descriptions.Item>
								)}
								{customerData.businessInfo.industry && (
									<Descriptions.Item label="Industry">
										{customerData.businessInfo.industry}
									</Descriptions.Item>
								)}
								{customerData.businessInfo.website && (
									<Descriptions.Item label="Website">
										<a
											href={customerData.businessInfo.website}
											target="_blank"
											rel="noopener noreferrer">
											{customerData.businessInfo.website}
										</a>
									</Descriptions.Item>
								)}
							</Descriptions>
						</>
					)}

					{/* Address Information */}
					{customerData.address && (
						<>
							<Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
								Address Information
							</Title>
							<Descriptions column={1} bordered>
								<Descriptions.Item label="Address">
									{[
										customerData.address.street,
										customerData.address.city,
										customerData.address.state,
										customerData.address.postalCode,
										customerData.address.country,
									]
										.filter(Boolean)
										.join(", ") || "No address provided"}
								</Descriptions.Item>
							</Descriptions>
						</>
					)}

					{/* Notes */}
					{customerData.notes && (
						<>
							<Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
								Notes
							</Title>
							<div style={{ padding: 16, backgroundColor: "#f5f5f5", borderRadius: 8 }}>
								<Text>{customerData.notes}</Text>
							</div>
						</>
					)}

					{/* Metadata */}
					{customerData.metadata && Object.keys(customerData.metadata).length > 0 && (
						<>
							<Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
								Metadata
							</Title>
							<pre
								style={{
									margin: 0,
									fontSize: "12px",
									backgroundColor: "#f5f5f5",
									padding: 16,
									borderRadius: 8,
								}}>
								{JSON.stringify(customerData.metadata, null, 2)}
							</pre>
						</>
					)}
				</Card>
			),
		},
		{
			key: "addresses",
			label: `Addresses (${customerData.addresses ? customerData.addresses.length : 0})`,
			children:
				customerData.addresses && customerData.addresses.length > 0 ? (
					<div style={{ display: "grid", gap: 16 }}>
						{customerData.addresses.map((address, index) => (
							<Card
								key={address._id || index}
								size="small"
								extra={
									<Space>
										<Button
											type="text"
											size="small"
											icon={<PiPencilDuotone />}
											onClick={() => handleEditAddress(address)}
										/>
										<Popconfirm
											title="Delete Address"
											description="Are you sure you want to delete this address?"
											onConfirm={() => handleDeleteAddress(address._id)}
											okText="Yes"
											cancelText="No">
											<Button type="text" danger size="small" icon={<PiTrashDuotone />} />
										</Popconfirm>
									</Space>
								}>
								<div style={{ display: "flex", alignItems: "start", gap: 12 }}>
									<PiMapPinDuotone style={{ fontSize: 16, color: "#666", marginTop: 2 }} />
									<div style={{ flex: 1 }}>
										<div style={{ marginBottom: 4 }}>
											<Text strong style={{ fontSize: 14 }}>
												{address.label}
											</Text>
										</div>
										<div style={{ marginBottom: 4 }}>
											<Text style={{ fontSize: 13, color: "#666" }}>
												{address.name} • {address.email} • {address.phone}
											</Text>
										</div>
										<div style={{ fontSize: 13, color: "#333" }}>
											{address.street1}
											{address.street2 && `, ${address.street2}`}, {address.city}, {address.state}{" "}
											{address.postalCode}, {address.country}
										</div>
									</div>
								</div>
							</Card>
						))}
						<Button type="dashed" icon={<PiPlusDuotone />} onClick={handleAddAddress}>
							Add Another Address
						</Button>
					</div>
				) : (
					<Empty
						description="No addresses found for this customer"
						image={<PiMapPinDuotone style={{ fontSize: 48, color: "#d9d9d9" }} />}>
						<Button type="primary" icon={<PiPlusDuotone />} onClick={handleAddAddress}>
							Add First Address
						</Button>
					</Empty>
				),
		},
		{
			key: "orders",
			label: `Orders (${orders.length})`,
			children:
				orders.length > 0 ? (
					<Table
						columns={orderColumns}
						dataSource={orders}
						loading={ordersLoading}
						rowKey="_id"
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
						}}
					/>
				) : (
					<Empty description="No orders found for this customer" />
				),
		},
		{
			key: "domain-registration-address",
			label: "Domain Registration Address",
			children:
				domaCustomers.length > 0 ? (
					<div style={{ display: "grid", gap: 16 }}>
						{domaCustomers.map((customer, index) => (
							<Card
								title={
									<Space>
										<span style={{ fontWeight: 500 }}>{customer.registrarCode}</span>
										<Text type="secondary">({customer.addressId})</Text>
									</Space>
								}
								key={customer._id || index}
								size="small">
								<pre style={{ margin: 0, fontSize: "12px", wordWrap: "break-word" }}>
									{JSON.stringify(customer.metadata, null, 2)}
								</pre>
							</Card>
						))}
					</div>
				) : (
					<Empty description="No domain registration address found for this customer">
						<Button type="primary" icon={<PiPlusDuotone />} onClick={handleAddDomaCustomer}>
							Add Domain Registration Address
						</Button>
					</Empty>
				),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/customers")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Customer: {customerData.name}
					</Title>
					<Button
						type="link"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/customers/${customerData._id}/edit`)}>
						Edit Customer
					</Button>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					// title={`Customer Details - ${customerData.name}`}
					extra={
						<Space>
							<Button onClick={() => navigate("/customers")} style={{ marginRight: 8 }}>
								Back to List
							</Button>
							<Button
								type="primary"
								icon={<PiPencilDuotone />}
								onClick={() => navigate(`/customers/${customerData._id}/edit`)}>
								Edit
							</Button>
						</Space>
					}>
					<Tabs items={tabItems} />
				</Card>
			</div>

			{/* Address Modal */}
			<Modal
				title={editingAddress ? "Edit Address" : "Add New Address"}
				open={addressModalVisible}
				onOk={handleAddressSubmit}
				onCancel={handleAddressModalCancel}
				confirmLoading={addressLoading}
				width={700}
				okText={editingAddress ? "Update" : "Add"}>
				<Form
					form={addressForm}
					layout="vertical"
					initialValues={{
						name: "",
						email: "",
						phone: "",
						label: "",
						street1: "",
						street2: "",
						city: "",
						state: "",
						postalCode: "",
						country: "Indonesia",
						countryCode: "ID",
					}}>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="name"
								label="Full Name"
								rules={[{ required: true, message: "Please enter full name" }]}>
								<Input placeholder="Enter full name" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="email"
								label="Email"
								rules={[
									{ required: true, message: "Please enter email" },
									{ type: "email", message: "Please enter a valid email" },
								]}>
								<Input placeholder="Enter email address" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="phone"
								label="Phone Number"
								rules={[{ required: true, message: "Please enter phone number" }]}>
								<Input placeholder="Enter phone number (e.g., +628123456789)" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="label"
								label="Address Label"
								rules={[{ required: true, message: "Please enter address label" }]}>
								<Select placeholder="Select address label">
									<Option value="Home">Home</Option>
									<Option value="Office">Office</Option>
									<Option value="Billing">Billing</Option>
									<Option value="Shipping">Shipping</Option>
									<Option value="Other">Other</Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="street1"
								label="Street Address 1"
								rules={[{ required: true, message: "Please enter street address" }]}>
								<Input placeholder="Enter street address" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="street2" label="Street Address 2 (Optional)">
								<Input placeholder="Enter additional address info" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={8}>
							<Form.Item
								name="city"
								label="City"
								rules={[{ required: true, message: "Please enter city" }]}>
								<Input placeholder="Enter city" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="state"
								label="State/Province"
								rules={[{ required: true, message: "Please enter state" }]}>
								<Input placeholder="Enter state/province" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="postalCode"
								label="Postal Code"
								rules={[{ required: true, message: "Please enter postal code" }]}>
								<Input placeholder="Enter postal code" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="country"
								label="Country"
								rules={[{ required: true, message: "Please select country" }]}>
								<Select placeholder="Select country" showSearch>
									<Option value="Indonesia">Indonesia</Option>
									<Option value="Malaysia">Malaysia</Option>
									<Option value="Singapore">Singapore</Option>
									<Option value="Thailand">Thailand</Option>
									<Option value="Philippines">Philippines</Option>
									<Option value="Vietnam">Vietnam</Option>
									<Option value="Other">Other</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="countryCode"
								label="Country Code"
								rules={[{ required: true, message: "Please enter country code" }]}>
								<Select placeholder="Select country code" showSearch>
									<Option value="ID">ID - Indonesia</Option>
									<Option value="MY">MY - Malaysia</Option>
									<Option value="SG">SG - Singapore</Option>
									<Option value="TH">TH - Thailand</Option>
									<Option value="PH">PH - Philippines</Option>
									<Option value="VN">VN - Vietnam</Option>
									<Option value="US">US - United States</Option>
									<Option value="AU">AU - Australia</Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>

			{/* Domain Customer Modal */}
			<Modal
				title="Add Domain Registration Address"
				open={domaModalVisible}
				onOk={handleDomaCustomerSubmit}
				onCancel={handleDomaModalCancel}
				confirmLoading={domaLoading}
				width={600}
				okText="Add">
				<Form
					form={domaForm}
					layout="vertical"
					initialValues={{
						registrarCode: "RDASH",
					}}>
					<Form.Item
						name="addressId"
						label="Select Address"
						rules={[{ required: true, message: "Please select an address" }]}>
						<Select placeholder="Select an address from customer's addresses">
							{customerData?.addresses?.map((address) => (
								<Option key={address._id} value={address._id}>
									<Space direction="horizontal">
										<div style={{ fontWeight: 500 }}>{address.label}</div>
										<div style={{ fontSize: 12, color: "#666" }}>
											{address.name} - {address.street1}, {address.city}, {address.state}
										</div>
									</Space>
								</Option>
							)) || []}
						</Select>
					</Form.Item>
					<Form.Item
						name="registrarCode"
						label="Registrar"
						rules={[{ required: true, message: "Please select a registrar" }]}>
						<Select placeholder="Select registrar">
							<Option value="RDASH">RDASH</Option>
							<Option value="SPACESHIP">SPACESHIP</Option>
						</Select>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default ViewCustomerPage;
